/**
 * Shared Audit Trail API
 * RTK Query endpoints for the immutable audit log.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export const auditTrailApi = createApi({
  reducerPath: 'auditTrailApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AuditLog', 'ApprovalRequest'],
  endpoints: (builder) => ({
    getAuditLogs: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 25,
          filters = {},
          action = '',
          objectType = '',
          dateFrom = '',
          dateTo = '',
          user = '',
          userEmail = '',
        } = args;

        const mergedFilters = {
          action: filters.action ?? action,
          contentType: filters.content_type ?? filters.contentType ?? objectType,
          dateFrom: filters.date_from ?? filters.dateFrom ?? dateFrom,
          dateTo: filters.date_to ?? filters.dateTo ?? dateTo,
          user: filters.user ?? user,
          userEmail: filters.user_email ?? filters.userEmail ?? userEmail,
        };

        return {
          url: '/api/v1/audit-trail/',
          params: {
            page,
            page_size: limit,
            action: mergedFilters.action || undefined,
            content_type: mergedFilters.contentType || undefined,
            date_from: mergedFilters.dateFrom || undefined,
            date_to: mergedFilters.dateTo || undefined,
            user: mergedFilters.user || undefined,
            user_email: mergedFilters.userEmail || undefined,
          },
        };
      },
      transformResponse: (response) => ({
        data: response?.results || [],
        total: response?.count || 0,
        next: response?.next,
        previous: response?.previous,
      }),
      providesTags: [{ type: 'AuditLog', id: 'LIST' }],
    }),

    getAuditLogById: builder.query({
      query: (id) => `/api/v1/audit-trail/${id}/`,
      providesTags: (result, error, id) => [{ type: 'AuditLog', id }],
    }),

    // Change Approval Requests
    getApprovalRequests: builder.query({
      query: ({ status, content_type } = {}) => ({
        url: '/api/v1/audit-trail/approvals/',
        params: { status, content_type },
      }),
      transformResponse: (r) => ({ data: r?.results ?? r ?? [], total: r?.count ?? 0 }),
      providesTags: [{ type: 'ApprovalRequest', id: 'LIST' }],
    }),

    // C2 (Task 13): the DETAIL record carries approval_request_id/approval_status (R19).
    getApprovalRequestById: builder.query({
      query: (id) => ({ url: `/api/v1/audit-trail/approvals/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'ApprovalRequest', id }],
    }),

    createApprovalRequest: builder.mutation({
      query: (body) => ({ url: '/api/v1/audit-trail/approvals/', method: 'POST', body }),
      invalidatesTags: [{ type: 'ApprovalRequest', id: 'LIST' }],
    }),

    approveRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/v1/audit-trail/approvals/${id}/approve/`,
        method: 'POST',
        body: { reason: reason || '' },
      }),
      invalidatesTags: ['ApprovalRequest'],
    }),

    rejectRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/v1/audit-trail/approvals/${id}/reject/`,
        method: 'POST',
        body: { reason: reason || '' },
      }),
      invalidatesTags: ['ApprovalRequest'],
    }),

    cancelRequest: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/audit-trail/approvals/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequest'],
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetAuditLogByIdQuery,
  useGetApprovalRequestsQuery,
  useGetApprovalRequestByIdQuery,
  useCreateApprovalRequestMutation,
  useApproveRequestMutation,
  useRejectRequestMutation,
  useCancelRequestMutation,
} = auditTrailApi;
export default auditTrailApi;
